import * as React from 'react'
import {
  Box,
  Form,
  ConfirmationModal,
  TextField,
  Grid,
  Typography,
} from 'cs-kevlar-react'
import { Formik } from 'formik'
import { EditModalActionBar, ConfirmDeleteModal } from 'components'
import { Spacing } from 'cs-kevlar-react/lib/es/theming/baseConstants'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { EditCustomerForm, EditCustomerFormSchema } from './EditCustomerForm'
import { Customer, CustomerSummary } from 'contexts/base'
import { Guid, CustomerAgencyViewModel } from '../../api'
import {
  useCustomers,
  useCustomerApi,
  usePageNotifications,
  useLogger,
  useCurrentCustomer,
} from 'hooks'
import { CustomerAction } from 'contexts/CurrentCustomerContext'
import { CustomerDetailPaneBaseProps } from './customerDetailPane'

interface EditCustomerProps extends CustomerDetailPaneBaseProps {
  customer: Customer | CustomerSummary
  onDeleted?: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    formFields: {
      padding: theme.spacing(10),
      // setting the spacing on the Grid adds to the width and subtracts from the margin, but not enough
      marginLeft: -2 * Spacing,
      marginRight: -2 * Spacing,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
  }),
)

export const EditCustomer: React.FunctionComponent<EditCustomerProps> = ({
  customer,
  onClose,
  onDeleted,
}) => {
  const classes = useStyles({})
  const { logError } = useLogger()

  const { dispatch: dispatchCustomerAction } = useCustomers()

  const { editCustomer, addCustomer, deleteCustomer } = useCustomerApi()

  const { dispatch: dispatchNotification } = usePageNotifications()

  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(
    false,
  )

  const isAdding = customer.id === 'NEW'
  const isEditing = customer.id !== 'NEW'

  const customerWithDefaults = {
    ...customer,
  }

  if (customerWithDefaults.notes === null) {
    customerWithDefaults.notes = ''
  }

  var dispatchCurrentCustomerAction: (_: CustomerAction) => void
  if (isEditing) {
    dispatchCurrentCustomerAction = useCurrentCustomer(customer.id).dispatch
  }

  const onSubmit = (values: Customer | CustomerSummary, formikActions: any) => {
    if (isEditing) onEdit(customer.id, values, onClose, formikActions)
    if (isAdding) onAdd(values, onClose, formikActions)
  }

  const onEdit = (
    customerAgencyId: Guid,
    values: Customer | CustomerSummary,
    onComplete: () => void,
    formikActions: any,
  ) => {
    editCustomer(customerAgencyId, values)
      .then((response) => {
        if (!response) return

        formikActions.setSubmitting(false)

        if (response.status === 200 && response.data.isSuccess) {
          const customerModel = {
            ...response.data.data,
            id: response.data.data.customerAgencyId,
          }

          dispatchCustomerAction({
            type: 'EditCustomer',
            payload: customerModel,
          })

          dispatchCurrentCustomerAction({
            type: 'Clear',
          })

          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Customer updated successfully.',
          })

          onComplete()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error editing the customer',
          })
        }
      })
      .catch((error) => {
        formikActions.setSubmitting(false)

        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error editing the customer',
        })
      })
  }

  const onAdd = (
    values: Customer | CustomerSummary,
    onComplete: () => void,
    formikActions: any,
  ) => {
    // remove the NEW id and customerAgencyId so that the server will generate a new GUID
    const newCustomer = Object.keys(values)
      .filter((key) => key !== 'id' && key !== 'customerAgencyId')
      .reduce((accum, current) => {
        const value = (values as any)[current]
        return {
          ...accum,
          [current]: value,
        } as Customer | CustomerSummary
      }, {} as Customer | CustomerSummary)

    addCustomer(newCustomer)
      .then((response) => {
        if (!response) return

        formikActions.setSubmitting(false)

        if (response.status === 200 && response.data.isSuccess) {
          const customerModel = {
            ...response.data.data,
            id: response.data.data.customerAgencyId,
          }

          dispatchCustomerAction({
            type: 'AddCustomer',
            payload: customerModel,
          })

          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Customer added successfully.',
          })

          onComplete()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error adding the customer',
          })
        }
      })
      .catch((error) => {
        formikActions.setSubmitting(false)

        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error adding the customer',
        })
      })
  }

  const onDelete = () => {
    deleteCustomer(customer.id)
      .then((response) => {
        if (!response) return

        if (response.status === 200 && response.data.isSuccess) {
          dispatchCustomerAction({
            type: 'DeleteCustomer',
            payload: customer.id,
          })

          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Customer deleted successfully.',
          })

          setIsConfirmingDelete(false)
          onClose()
          onDeleted && onDeleted()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error deleting the customer',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error deleting the customer',
        })
      })
  }

  return (
    <React.Fragment>
      <Formik
        initialValues={customerWithDefaults}
        validationSchema={EditCustomerFormSchema}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
            <Box className={classes.formFieldContainer}>
              <Grid
                container
                spacing={Spacing}
                direction="column"
                alignItems="flex-start"
                justifyContent="center"
                className={classes.formFields}
              >
                <EditCustomerForm formikProps={formikProps} />
              </Grid>
            </Box>
            <Box className={classes.spacer} />
            <EditModalActionBar
              canDelete={isEditing}
              onDelete={() => setIsConfirmingDelete(true)}
              onCancel={onClose}
              formikProps={formikProps}
              deleteText="Delete Customer"
              saveText="Save"
            />
          </Form>
        )}
      </Formik>
      {isEditing && (
        <ConfirmDeleteModal
          onCancel={() => setIsConfirmingDelete(false)}
          onConfirm={onDelete}
          open={isConfirmingDelete}
          line1Text="Please confirm you wish to delete this customer.  This action cannot be reversed."
          line2Text={`Customer: ${customer.name}`}
          inputLabelText="Please enter DELETE to confirm deletion of this customer."
        />
      )}
    </React.Fragment>
  );
}
